import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight2 } from 'iconsax-reactjs';
import type { ModuleId } from '@pipyar/core';
import { ModuleWeight } from '@/components/ModuleWeight';
import { cn } from '@/lib/utils';

interface WidgetCardProps {
  /** The module page the whole card links to. */
  to: string;
  icon: ReactNode;
  title: string;
  module: ModuleId;
  /** The widget's verdict or status chip, shown under the title. */
  badge?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * The shell every dashboard widget sits in: one column of the board.
 *
 * Built for a narrow, full-height column rather than a wide tile. The header
 * is two short lines — icon, title, arrow; then weight and verdict — because
 * a single line of all five overflowed at column width. The body fills the
 * rest of the column and scrolls inside it, so a long list never stretches
 * one column past the others.
 */
export function WidgetCard({ to, icon, title, module, badge, children, className }: WidgetCardProps) {
  return (
    <Link
      to={to}
      className={cn(
        'glass glass-hover group flex h-full min-h-0 flex-col overflow-hidden rounded-2xl',
        className,
      )}
    >
      <div className="shrink-0 border-b border-border/60 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
            {icon}
          </div>
          <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{title}</h2>
          <ArrowRight2
            size={15}
            className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          />
        </div>
        <div className="mt-1.5 flex min-h-5 flex-wrap items-center gap-1.5">
          <ModuleWeight id={module} />
          {badge}
        </div>
      </div>

      <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto">{children}</div>
    </Link>
  );
}
